import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Directory, DownloadFileResult, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';

import { noop } from 'rxjs';

import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private toastService: ToastService,
    private translateService: TranslateService,
    private loadingController: LoadingController
  ) {}

  androidDownload(url: string, title: string): void {
    void this.startLoader();

    const currentDate = new Date().toLocaleString().replace(/[\s,:/]/g, '-');

    Filesystem.downloadFile({
      url,
      path: `${title} - ${currentDate}.pdf`,
      directory: Directory.Documents,
      headers: {
        'access-token': `${localStorage.getItem('accessToken')}`,
        client: `${localStorage.getItem('client')}`,
        'content-type': 'application/json',
        expiry: `${localStorage.getItem('expiry')}`,
        'token-type': `${localStorage.getItem('tokenType')}`,
        uid: `${localStorage.getItem('uid')}`
      }
    })
      .then((res: DownloadFileResult) => {
        this.dismissLoader();
        FileOpener.openFile({ path: res.path, mimeType: 'application/pdf' })
          .then(noop)
          .catch(() => {
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
          });
      })
      .catch(() => {
        this.dismissLoader();
        void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
      });
  }

  iosDownload(url: string, title: string, addAuthHeaders: boolean) {
    void this.startLoader();

    const headers = {
      'access-token': `${localStorage.getItem('accessToken')}`,
      client: `${localStorage.getItem('client')}`,
      'content-type': 'application/json',
      expiry: `${localStorage.getItem('expiry')}`,
      'token-type': `${localStorage.getItem('tokenType')}`,
      uid: `${localStorage.getItem('uid')}`
    };

    this.http.get(url, { responseType: 'blob', headers: addAuthHeaders ? headers : {} }).subscribe(
      async (event) => {
        const base64 = await this.convertBlobToBase64(event);
        const savedFile = await Filesystem.writeFile({
          path: title,
          data: base64,
          directory: Directory.Documents
        });

        FileOpener.openFile({ path: savedFile.uri, mimeType: 'application/pdf' })
          .then(noop)
          .catch((err) => {
            Sentry.captureException(new Error('Document couldn`t be opened.'), {
              extra: { ...err, documentName: title, platform: 'ios', type: 'fileOpener' }
            });
          });
        this.dismissLoader();
      },
      (err) => {
        Sentry.captureException(new Error('Document couldn`t be opened.'), {
          extra: { ...err, documentName: title, platform: 'ios', type: 'httpRequest' }
        });
        this.dismissLoader();
      }
    );
  }

  private async startLoader(): Promise<void> {
    const loading = await this.loadingController.create({});
    await loading.present();
  }

  private dismissLoader(): void {
    this.loadingController
      .getTop()
      .then((value) => {
        if (value) {
          void this.loadingController.dismiss();
        }
      })
      .catch(noop);
  }

  private convertBlobToBase64 = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      this.zone.run(() => {
        const reader = this.getFileReader();
        reader.onerror = reject;
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(blob);
      });
    });

  // Cordova issue Angular zones: https://github.com/ionic-team/capacitor/issues/1564#issuecomment-538200971
  private getFileReader(): FileReader {
    const fileReader = new FileReader();
    const zoneOriginalInstance = (fileReader as any)['__zone_symbol__originalInstance'];
    return zoneOriginalInstance || fileReader;
  }
}
